import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NotifierService } from 'angular-notifier';

import { PartnerDto } from 'src/app/domain/models/dto/partner.dto';
import { CorporateAccountService } from 'src/app/shared/services/corporate-account.service';
import { cnpjValidator } from 'src/app/shared/validators/form-validators';

@Component({
  selector: 'app-partners',
  templateUrl: './partners.component.html',
  styleUrls: ['./partners.component.scss'],
})
export class PartnersComponent implements OnInit {
  private readonly notifier: NotifierService;

  showTables = false;
  havePartners = false;
  showPartnersError = false;
  loading: boolean = false;
  loadingTable: boolean = false;

  tableDisplayedColumns: string[] = [
    'companyName',
    'cnpj',
    'spread',
    'tariff',
    'institutionalSpread',
    'agencyNumber',
    'spreadBanking',
    'action',
  ];
  partners: PartnerDto[];
  allPartners: PartnerDto[] = [];
  partnerForm: FormGroup;
  pageSize: number = 10;
  pageNumber: number = 0;
  searchControl = new FormControl('');

  get partner(): PartnerDto {
    return this.partnerForm.value as PartnerDto;
  }

  get partnerFormControl() {
    return this.partnerForm.controls;
  }

  constructor(
    private readonly companyService: CorporateAccountService,
    private readonly formBuilder: FormBuilder,
    notifierService: NotifierService,
  ) {
    this.notifier = notifierService;

    this.partnerForm = this.formBuilder.group({
      companyName: ['', [Validators.required, Validators.maxLength(144)]],
      cnpj: ['', [Validators.required, cnpjValidator]],
      agencyNumber: ['',[Validators.required, Validators.pattern(/^[0-9]*$/), Validators.maxLength(10)]],
      spread: [null, [Validators.required, Validators.min(0), Validators.max(100)]],
      institutionalSpread: [null,[Validators.required, Validators.min(0), Validators.max(100)]],
      spreadBanking: [true, [Validators.required]],
      tariff: [null, [Validators.required, Validators.min(0)]],
    });
  }

  ngOnInit(): void {
    this.companyService.listenerUpdate.subscribe(() => {
      this.loadPartners(this.pageNumber, this.pageSize);
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(() => this.applySearch());
  }

  reloadPage() {
    window.location.reload();
  }

  loadPartners(page: number, pageSize: number) {
    this.loadingTable = true;
    this.companyService
      .getPartnersTable(page + 1, pageSize)
      .pipe(finalize(() => (this.loadingTable = false)))
      .subscribe({
        next: (result) => {
          this.allPartners = result.data ?? [];
          this.havePartners = this.allPartners.length > 0;
          this.showTables = this.havePartners;
          this.showPartnersError = false;
          this.applySearch();
        },
        error: () => {
          this.havePartners = false;
          this.showTables = false;
          this.showPartnersError = true;
        },
      });
  }

  applySearch() {
    const term = (this.searchControl.value || '').trim().toLowerCase();
    if (!term) {
      this.partners = [...this.allPartners];
      return;
    }
    const normalize = (value: string) =>
      (value || '').toLowerCase().replace(/[.\-\/\s]/g, '');
    const normalizedTerm = normalize(term);
    this.partners = this.allPartners.filter((p) => {
      return (
        (p.companyName || '').toLowerCase().includes(term) ||
        normalize(p.cnpj).includes(normalizedTerm) ||
        (p.agencyNumber || '').toLowerCase().includes(term)
      );
    });
  }

  onFilter() {
    this.applySearch();
  }

  sendPartner() {
    this.loading = true;
    if (this.partnerForm.invalid) {
      this.partnerForm.markAllAsTouched();
      return;
    }

    const payload: PartnerDto = {
      ...this.partner,
      spread: this.partner.spread != null ? this.partner.spread / 100 : null,
      institutionalSpread:
        this.partner.institutionalSpread != null
          ? this.partner.institutionalSpread / 100
          : null,
    };

    this.companyService
      .createPartner(payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.resetForm();
          this.showPartnersError = false;
          this.companyService.updatePartnerView();
          this.notifier.notify('success', 'Parceiro criado com sucesso!');
        },
      });
  }

  resetForm() {
    this.partnerForm.reset();
    this.searchControl.reset('');
  }
}
